import { useRef } from 'react';
import { tokens } from '@hotjar/hotsauce';
import { useActor } from '@xstate/react';
import type { InterpreterFrom } from 'xstate';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useAccountTrialInfo } from '@hotjar/account';
import reviewOrderGraphic from '../../../resources/images/review-order.svg';
import { Header } from '../shared/header/Header';
import type { States } from '../../state-machine/checkout.machine.types';
import { ManageSubscriptions } from './manage-subscriptions/ManageSubscriptions';
import { InvoiceDetails } from './invoice-details/InvoiceDetails';
import { PaymentDetails } from './payment-details/PaymentDetails';
import * as S from './EditOrderContainer.styles';
import { ReviewOrder } from './review-order/ReviewOrder';
import { CostChangesCard } from './cost-changes/CostChanges';
import { Body, Container } from '../shared/shared.styles';
import type { checkoutMachine } from '../../state-machine/checkout.machine';
import type { CheckoutStep } from './steps/steps.utils';
import { getCheckoutSteps } from './steps/steps.utils';
import { Footer } from './footer/Footer';
import { CostChangesSummary } from './cost-changes/cost-changes-summary/CostChangesSummary';
import { useServiceResourceList } from '~/subscription-management/features/service-resource-list';
import { orgsHaveActiveChanges } from './cost-changes/utils';
import type { PricePreviewMachine } from '../../state-machine/child-machines/preview-price.machine';
import {
  getTrialRemainingDays,
  isOnActiveTrial,
  isOnEligibleTrial,
} from '~/subscription-management/utils/trial/trial.utils';
import { prettifyDate } from '~/subscription-management/utils/date.utils';
import { FORM_ID } from '~/subscription-management/components/invoice-details/form/InvoiceDetailsForm';
import type { paymentDetailsMachine } from '../../state-machine/child-machines/payment-details.machine';

interface Props {
  checkoutService: InterpreterFrom<typeof checkoutMachine>;
}

export function EditOrderContainer({ checkoutService }: Props): JSX.Element {
  const { t: tHeader } = useTranslationWithPrefix('checkout:header');
  const { t: tChurn } = useTranslationWithPrefix('checkout:churn');
  const { t: tOnTrialAlert } = useTranslationWithPrefix('checkout:trial-alert');

  const [state, send] = useActor(checkoutService);

  const previewMachine = checkoutService.children.get('PricePreviewMachine') as InterpreterFrom<
    typeof PricePreviewMachine
  >;
  const [previewState] = useActor(previewMachine);

  const paymentMachine = checkoutService.children.get('paymentDetailsMachine') as
    | InterpreterFrom<typeof paymentDetailsMachine>
    | undefined;

  const trialInfo = useAccountTrialInfo();

  const {
    costs,
    canProceed,
    billingPeriod,
    allowancesToAdd,
    allEffectiveAllowances,
    allRenewedAllowances,
    userId,
    accountId,
    hasActiveSubscription,
    loadedFromDraftOrder,
    subscription,
    customer: { currency, billingAddress, companyName, taxNumber, currentPeriodCostSummary },
    customerId,
    completedSteps,
    accountDiscounts,
  } = state.context;

  const { allResources, loading: resourcesLoading } = useServiceResourceList({
    userId,
    accountId,
    productAllowances: {
      effectiveNow: allEffectiveAllowances,
      renewWith: allRenewedAllowances,
    },
    billingPeriod,
    currency,
    costs: {
      current: currentPeriodCostSummary,
      future: costs?.renewWith ?? currentPeriodCostSummary,
    },
  });

  const stepBody = useRef<HTMLDivElement>(null);
  const nextChargeDate =
    subscription?.currentPeriodEndDate !== undefined
      ? prettifyDate(subscription?.currentPeriodEndDate, billingAddress?.country)
      : undefined;

  const steps = getCheckoutSteps(tHeader, completedSteps);

  const goToState = (nextState: CheckoutStep) => {
    send(nextState.event ?? 'SETUP_SUBSCRIPTION');
  };

  const showCostChanges = !state.matches({ editOrder: { steps: 'reviewOrder' } });
  const isPricePreviewLoading = previewState.matches('getCostPreview');
  const isUpdateCustomerDetailsLoading = state.matches({
    editOrder: { steps: { invoiceDetails: 'updateCustomerDetails' } },
  });

  const showTaxes =
    !state.matches({ editOrder: { steps: 'invoiceDetails' } }) &&
    (state.matches({ editOrder: { steps: 'paymentDetails' } }) || customerId !== undefined) &&
    costs.renewWith?.taxAmount !== 0;

  const getOnTrialAlertMessage = (): string => {
    if (isOnActiveTrial(trialInfo)) {
      return tOnTrialAlert('active-trial-message', {
        days: getTrialRemainingDays(trialInfo),
      });
    }

    if (isOnEligibleTrial(trialInfo)) {
      return tOnTrialAlert('eligible-trial-message');
    }

    return '';
  };

  const focusFirstFocusableElement = () => {
    const firstFocusableElement = stepBody.current?.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ) as HTMLInputElement | null;
    firstFocusableElement?.focus();
  };

  return (
    <Container direction="column">
      <Header<Extract<States, string>>
        loading={false}
        steps={steps}
        currentStep={steps.find((step) => state.matches(step.id)) ?? steps[0]}
        onStepClick={goToState}
      />
      <Body ref={stepBody}>
        <S.Content paddingRight={showCostChanges ? undefined : `${tokens.spacer.x4}`}>
          {state.matches({ editOrder: { steps: 'editSubscription' } }) && (
            <ManageSubscriptions
              allResources={allResources}
              loading={resourcesLoading}
              isPricePreviewLoading={isPricePreviewLoading}
              updatedProductAllowances={allowancesToAdd}
              billingPeriod={billingPeriod}
              currency={currency}
              hasActiveSubscription={hasActiveSubscription}
              shouldDisableCheckout={false}
              loadedFromDraftOrder={loadedFromDraftOrder}
              subscriptionEndDate={nextChargeDate}
              onBillingPeriodChanged={(updatedBillingPeriod) => {
                send({ type: 'UPDATE_BILLING_PERIOD', billingPeriod: updatedBillingPeriod });
              }}
              onPlanChange={(allowance) => {
                send({ type: 'UPDATE_ALLOWANCES', allowances: [allowance] });
              }}
              onProceed={() => send('NEXT')}
              canProceed={canProceed}
            />
          )}
          {state.matches({ editOrder: { steps: 'invoiceDetails' } }) && (
            <InvoiceDetails
              defaultValues={{ ...billingAddress, companyName, taxNumber }}
              onSubmit={(formInputs) => send({ type: 'NEXT', formInputs })}
              formId={FORM_ID}
              accountId={accountId}
              hasError={
                state.matches('editOrder.steps.invoiceDetails.previewError') ||
                state.matches('editOrder.steps.invoiceDetails.updateError')
              }
              billingAddress={billingAddress}
            />
          )}
          {state.matches({ editOrder: { steps: 'paymentDetails' } }) &&
            paymentMachine !== undefined && (
              <PaymentDetails
                onSuccess={({ refId }) => {
                  send({
                    type: 'UPDATE_PAYMENT_DETAILS',
                    refId,
                  });
                }}
                countryCode={billingAddress?.country}
                accountId={accountId}
                paymentDetailsService={paymentMachine}
              />
            )}
          {state.matches({ editOrder: { steps: 'reviewOrder' } }) && (
            <ReviewOrder
              context={state.context}
              loading={isPricePreviewLoading}
              allResources={allResources}
              accountDiscounts={accountDiscounts}
              nextChargeDate={nextChargeDate}
              editSubscriptions={() => send('SETUP_SUBSCRIPTION')}
              navigateToInvoiceDetails={() => send('INVOICE_DETAILS')}
              navigateToPaymentDetails={() => send('PAYMENT_DETAILS')}
            />
          )}
        </S.Content>
        <S.Sidebar direction="column" spacing="x3">
          {showCostChanges && (
            <>
              <CostChangesCard
                costChanges={{
                  priceWithTax: costs.renewWith?.amountWithTax ?? 0,
                  priceWithoutTax: costs.renewWith?.amountWithoutTax ?? 0,
                  priceTax: costs.renewWith?.taxAmount ?? 0,
                  currency,
                  accountDiscounts,
                }}
                updated={canProceed || state.matches('editOrder.steps.invoiceDetails')}
                loading={isPricePreviewLoading}
                billingPeriod={billingPeriod}
                showTaxes={showTaxes}
              />
              {!!allResources && orgsHaveActiveChanges(allResources, allowancesToAdd) && (
                <CostChangesSummary
                  defaultExpanded={state.matches({ editOrder: { steps: 'editSubscription' } })}
                  allResources={allResources}
                  updatedProducts={allowancesToAdd}
                  billingPeriod={billingPeriod}
                  currency={currency}
                  loading={isPricePreviewLoading}
                />
              )}

              {trialInfo?.activeOrEligible === true && (
                <S.OnTrialAlert>{getOnTrialAlertMessage()}</S.OnTrialAlert>
              )}

              {hasActiveSubscription && (
                <S.CancelButton
                  kind="ghost_standout"
                  onClick={() => send({ type: 'NEXT', isChurning: true })}
                >
                  {tChurn('cancel-subscription')}
                </S.CancelButton>
              )}
            </>
          )}
          {!showCostChanges && <img src={reviewOrderGraphic} alt="" />}
        </S.Sidebar>
      </Body>
      <Footer
        context={state.context}
        matchState={state.matches}
        send={send}
        isLoading={isPricePreviewLoading || isUpdateCustomerDetailsLoading}
        trialInfo={trialInfo}
        isDisabled={false}
        restoreFocus={focusFirstFocusableElement}
      />
    </Container>
  );
}
